import { sidebarStore, synapse } from 'services/state'
import { BASE_URL } from 'config'
import { saveAs } from 'file-saver'
import { noop, trim, trimStart } from 'lodash'
import {
  ctasMap,
  DELETE,
  DELETE_ERROR,
  DELETE_SUCCESS,
  EXPORT,
  EXPORT_ERROR,
  EXPORT_SUCCESS,
  IMPORT,
  IMPORT_ERROR,
  IMPORT_SUCCESS,
  MIN_TIME,
  SCENARIO,
  UPLOAD_ERROR,
  UPLOAD_SUCCESS
} from './constants'
import { DELETED } from 'pages/Scenario/constants'
import eventsDispatcher from 'pages/InputView/eventsDispatcher'
import { updateInstanceWorkingTableCount } from 'src/App/actions'

const refreshTable = () => {
  eventsDispatcher?.refreshEvent({ overwrite: true })
  const sidebarType = sidebarStore.get((state) => state.type)

  // we need to wait to have wtIds saved in store to have wts count
  if (sidebarType === 'workingTables') {
    updateInstanceWorkingTableCount()
  }
}

export const getSafeData = (data) => ({
  ...data,
  action: {
    ...(data.action || {}),
    params: {
      ...(data.action?.params || {})
    }
  }
})

export const getNotificationLabels = (props, tabProgress) => {
  const { action, userName, type, name, message } = props

  switch (type) {
    case EXPORT: {
      return {
        title: tabProgress ? name : `NotificationPanelPlatform:${action.label}`,
        message: tabProgress
          ? `NotificationPanelPlatform:${message}`
          : action.message
      }
    }
    case IMPORT: {
      return {
        title: tabProgress ? name : `NotificationPanelPlatform:${action.label}`,
        message: tabProgress
          ? `NotificationPanelPlatform:${message}`
          : action.message
      }
    }
    case DELETE: {
      return {
        title: tabProgress
          ? `Sidebar:${name}`
          : `NotificationPanelPlatform:${action.label}`,
        message: tabProgress
          ? `NotificationPanelPlatform:${message}`
          : action.message
          ? `Sidebar:${action.message}`
          : ''
      }
    }
    case SCENARIO: {
      return {
        title: action.params?.redirect?.scenarioName
          ? `Scenario ${action.params?.redirect?.scenarioName}`
          : '',
        message: `NotificationPanelPlatform:Scenario.${action?.label}`,
        tParams: { userName }
      }
    }
    default: {
      // If it is an generic notification, it must be taken into account that it can refer to a scenario
      return {
        title:
          action?.params && action?.params?.scenarioName
            ? `Scenario ${action?.params?.scenarioName}`
            : action?.params && action?.params?.redirect?.scenarioName
            ? action?.params?.redirect?.scenarioName
            : name || '',
        message:
          action && action?.label
            ? action?.params && action?.params?.redirect?.scenarioName
              ? `NotificationPanelPlatform:Scenario.${action?.label}`
              : `NotificationPanelPlatform:${action?.label}`
            : message
            ? `NotificationPanelPlatform:${message}`
            : '',
        tParams: { userName }
      }
    }
  }
}

export const getCtasMap = ({ history, openModal }) => ({
  openModal: (modalType, data) => openModal(modalType, data),
  redirect: (url, type) => {
    // We need to refresh table when url change or, if we are in the same route,
    // when we click on the notification action
    if (type === IMPORT_SUCCESS) {
      refreshTable()
    }
    return history.push(url)
  },
  download: async (url) => {
    const formattedUrl = encodeURI(url) // removing special characters from url

    const { body, fileName } = await synapse.fetch(
      `${BASE_URL}/${trimStart(formattedUrl, '/')}`
    )
    // Backend needs to send "Access-Control-Expose-Headers: Content-Disposition" header to get the file name
    saveAs(body, fileName ? trim(fileName, '"') : 'file.txt')
  }
})

// Fix me: we need to have a more robust way to determine ctas based on type, not checking individual parameters
export const makeGetCta =
  ({ ctas }) =>
  ({ action, type }, wtUrlParam) => {
    if (action) {
      const {
        label,
        params: { request = {} }
      } = action

      if (type === IMPORT || type === DELETE || type === EXPORT) {
        return actionsByLabel(action, ctas, wtUrlParam)
      }

      if (type === SCENARIO) {
        const { scenarioName } = request

        if (scenarioName && label !== DELETED) {
          return () =>
            ctas.redirect(`/scenario/management?title=${scenarioName}`)
        }
      }
    }
  }

export const hasAutoCta = (timeDiff, type, mapAutomated) => {
  // For specific types we have dafault behaviours
  // ('scenario' type never triggers the cta, while the export type always triggers it)
  if (mapAutomated[type]) {
    return mapAutomated[type]
  } else {
    // For the other cases we activate the cta based on the response time (<2s)
    return timeDiff < MIN_TIME
  }
}

const actionsByLabel = ({ label, params = {} }, ctas, wtUrlParam) => {
  switch (label) {
    case IMPORT_SUCCESS: {
      const { redirect = {}, cta = {} } = params
      const { instance, wt, documentType } = redirect
      const { type } = cta
      if (!wt || !documentType) {
        return noop
      }
      const url = `/instances/${instance}/workingtables/${wt}/${documentType}/documents`
      const func = ctas[ctasMap[type]] || ctas.redirect
      // We have to automatically refresh the table if we are in the same wt as the wt we imported.
      // Otherwise it will be the redirect to refresh the table
      if (wtUrlParam === wt) {
        refreshTable()
      }

      return () => func(url, IMPORT_SUCCESS)
    }
    case IMPORT_ERROR: {
      const { redirect = {}, cta = {} } = params
      const { instance, wt, documentType } = redirect
      const { errors, type } = cta
      const url = `/instances/${instance}/workingtables/${wt}/${documentType}/documents`

      const func = ctas[ctasMap[type]] || ctas.openModal

      return () =>
        func('importError', {
          errors,
          instance,
          wt,
          documentType,
          tryAgain: () => (!wt || !documentType ? noop : ctas.redirect(url))
        })
    }
    case UPLOAD_ERROR: {
      const { /* redirect = {}, */ cta = {} } = params
      const { errors, type } = cta
      const url = `/scenario/management`

      const func = ctas[ctasMap[type]] || ctas.openModal

      return () =>
        func('uploadError', {
          errors,
          tryAgain: () => ctas.redirect(url)
        })
    }
    case UPLOAD_SUCCESS: {
      return noop
    }
    case DELETE_SUCCESS: {
      const { redirect = {}, cta = {} } = params
      const { instance, wt, documentType } = redirect
      const { type } = cta
      if (!wt || !documentType) {
        return noop
      }
      const url = `/instances/${instance}/workingtables/${wt}/${documentType}/documents`
      const func = ctas[ctasMap[type]] || ctas.redirect

      return () => func(url)
    }
    case DELETE_ERROR: {
      const { redirect = {}, cta = {} } = params
      const { instance, wt, documentType } = redirect
      const { errors, type } = cta
      const url = `/instances/${instance}/workingtables/${wt}/${documentType}/documents`
      const func = ctas[ctasMap[type]] || ctas.openModal
      return () =>
        func('documentDeletionErrors', {
          deletionErrors: errors,
          tryAgain: () => (!wt || !documentType ? noop : ctas.redirect(url))
        })
    }
    case EXPORT_SUCCESS: {
      const { cta = {} } = params
      const { url, type } = cta
      const func = ctas[ctasMap[type]] || ctas.download

      return () => func(url)
    }
    case EXPORT_ERROR: {
      const { cta = {}, redirect = {} } = params
      const { instance, wt, documentType } = redirect
      const { type } = cta
      if (!wt || !documentType) {
        return noop
      }
      const url = `/instances/${instance}/workingtables/${wt}/${documentType}/documents`
      const func = ctas[ctasMap[type]] || ctas.download
      return () => func(url)
    }
    default:
      break
  }
}

export const buildNewNotification = (
  { type, requestFe },
  currentUser,
  notificationData
) => {
  const { result, cta, errors } = notificationData
  return {
    timestamp: Date.now(),
    userName: currentUser,
    action: {
      label: result,
      params: {
        redirect: {
          instance: requestFe.instance,
          wt: requestFe.wt,
          documentType: requestFe.documentType
        },
        request: requestFe,
        cta: {
          type: cta,
          url: null,
          fileName: null,
          errors
        }
      }
    },
    type: type?.toLowerCase()
  }
}
